import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { Calendar, Plus, Trash2, Upload, Users, MapPin, ChevronRight, FileSpreadsheet, Sparkles, Copy, Check, Archive, RotateCcw, QrCode, Image, Download, Link2, Mail, Send, HelpCircle, Pencil, X, Play, Clock } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import GuestQrPrintModal from "../components/GuestQrPrintModal";

// Sikker kopiering — falder tilbage til document.execCommand når Clipboard API er blokeret (fx i preview-iframes)
const safeCopy = (text) => {
  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text || '';
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('execCommand returned false'));
      } catch (e) { reject(e); }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text || '').then(resolve).catch(fallback);
    } else {
      fallback();
    }
  });
};

export default function EventSelector({ user, onLogout, onSelectEvent }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [newEventDesc, setNewEventDesc] = useState("");
  const [newEventAddress, setNewEventAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Slet', confirmVariant: 'destructive' });
  
  // Event-kopiering state
  const [copyFromEventId, setCopyFromEventId] = useState("");
  const [copyOptions, setCopyOptions] = useState({
    copy_zones: false,
    copy_teams: false,
    copy_shifts: false,
    copy_volunteers: false,
    copy_materiel: false,
    copy_lager: false,
    copy_stamdata: false,
    copy_notifications: false,
    copy_messages: false,
    copy_info: false,
    copy_deadlines: false,
  });
  
  // Edit event state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editQrPrefix, setEditQrPrefix] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  
  // Demo sessions state
  const [demoSessions, setDemoSessions] = useState([]);
  const [demoLinks, setDemoLinks] = useState([]);
  const [landingDemoEventId, setLandingDemoEventId] = useState(null);
  
  const token = localStorage.getItem("token");
  
  // Logo og QR state
  const [showQrModal, setShowQrModal] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [newEventLogo, setNewEventLogo] = useState(null);
  
  // Invitation state
  const [showInviteModal, setShowInviteModal] = useState(null); // event id
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("admin_assistant");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  // Gæsteadgang state
  const [showGuestModal, setShowGuestModal] = useState(null);
  const [guests, setGuests] = useState([]);
  const [newGuestEmail, setNewGuestEmail] = useState("");
  const [newGuestName, setNewGuestName] = useState("Gæst");
  const [newGuestExpiry, setNewGuestExpiry] = useState("24");
  const [newGuestCustomEndDate, setNewGuestCustomEndDate] = useState(""); // YYYY-MM-DD når expiry === "custom"
  // Udvidet gæste-felter
  const [newGuestValidFrom, setNewGuestValidFrom] = useState(""); // local datetime string, tomt = nu
  const [newGuestDirectLogin, setNewGuestDirectLogin] = useState(true);
  const [newGuestVisibleLayers, setNewGuestVisibleLayers] = useState({
    resource_bins: false,
    resource_islands: false,
    maxicontainers: false,
    work_zones: false,
    zones: false,
    gps_trackers: false,
    food_drinks: false,
    attention_points: false,
    analytics_module: false,
  });
  // QR-print state
  const [qrPrintGuest, setQrPrintGuest] = useState(null); // gæst-objekt der skal printes
  // Rediger eksisterende gæst (genbruger oprettelses-formularen, men beholder QR/kode/email)
  const [editingGuest, setEditingGuest] = useState(null);

  // Reset oprettelses-felter til defaults
  const resetGuestForm = () => {
    setNewGuestEmail("");
    setNewGuestName("Gæst");
    setNewGuestExpiry("24");
    setNewGuestCustomEndDate("");
    setNewGuestValidFrom("");
    setNewGuestDirectLogin(true);
    setNewGuestVisibleLayers({
      resource_bins: false, resource_islands: false, maxicontainers: false,
      work_zones: false, zones: false, gps_trackers: false,
      food_drinks: false, attention_points: false, analytics_module: false,
    });
    setEditingGuest(null);
  };

  // Beregn duration_hours fra brugerdefineret slutdato (slutter kl 23:59 valgte dag)
  const computeCustomDurationHours = (validFromISO, endDateYMD) => {
    if (!endDateYMD) return null;
    const startMs = validFromISO ? new Date(validFromISO).getTime() : Date.now();
    // 23:59:59 lokal tid på den valgte slutdato
    const [y, m, d] = endDateYMD.split('-').map(Number);
    if (!y || !m || !d) return null;
    const endMs = new Date(y, m - 1, d, 23, 59, 59).getTime();
    const diffH = Math.max(1, Math.round((endMs - startMs) / 3600000));
    return diffH;
  };

  // Indlæs en eksisterende gæst i formularen til redigering
  const startEditGuest = (guest) => {
    setEditingGuest(guest);
    setNewGuestEmail(guest.email || "");
    setNewGuestName(guest.name || "Gæst");
    setNewGuestExpiry(guest.duration_hours === 0 ? '0' : String(guest.duration_hours || 24));
    setNewGuestCustomEndDate(""); // nulstilles — admin vælger eksplicit ved redigering hvis ønsket
    // Konvertér ISO til "YYYY-MM-DDTHH:mm" til datetime-local input
    if (guest.valid_from) {
      const d = new Date(guest.valid_from);
      const pad = (n) => String(n).padStart(2, '0');
      setNewGuestValidFrom(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } else {
      setNewGuestValidFrom("");
    }
    setNewGuestDirectLogin(!!guest.direct_login_enabled);
    setNewGuestVisibleLayers({
      resource_bins: !!guest.visible_layers?.resource_bins,
      resource_islands: !!guest.visible_layers?.resource_islands,
      maxicontainers: !!guest.visible_layers?.maxicontainers,
      work_zones: !!guest.visible_layers?.work_zones,
      zones: !!guest.visible_layers?.zones,
      gps_trackers: !!guest.visible_layers?.gps_trackers,
      food_drinks: !!guest.visible_layers?.food_drinks,
      attention_points: !!guest.visible_layers?.attention_points,
      analytics_module: !!guest.visible_layers?.analytics_module,
    });
  };

  const saveGuestEdit = async (eventId) => {
    if (!editingGuest) return;
    try {
      let validFromISO;
      if (newGuestValidFrom) {
        const dt = new Date(newGuestValidFrom);
        if (!isNaN(dt.getTime())) validFromISO = dt.toISOString();
      }
      // Beregn duration_hours — håndtér "Brugerdefineret dato" specifikt
      let durationHours;
      if (newGuestExpiry === 'custom') {
        durationHours = computeCustomDurationHours(validFromISO, newGuestCustomEndDate);
        if (!durationHours || durationHours < 1) {
          toast.error("Vælg en gyldig slutdato længere fremme i tiden");
          return;
        }
      } else if (newGuestExpiry === '' || newGuestExpiry == null) {
        durationHours = 24;
      } else {
        durationHours = parseInt(newGuestExpiry, 10) || 0;
      }
      const payload = {
        name: newGuestName.trim() || 'Gæst',
        duration_hours: durationHours,
        direct_login_enabled: !!newGuestDirectLogin,
        visible_layers: newGuestVisibleLayers,
      };
      if (validFromISO) payload.valid_from = validFromISO;
      // Kun opdater email hvis admin har ændret den (så vi ikke ved et uheld erstatter auto-genererede direct-emails)
      if (newGuestEmail && newGuestEmail.trim() && newGuestEmail.trim() !== editingGuest.email) {
        payload.email = newGuestEmail.trim();
      }
      await axios.put(`${API}/guests/${editingGuest.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Gæsteadgang opdateret — QR-kode, email og kode beholdt');
      resetGuestForm();
      loadGuests(eventId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fejl ved opdatering');
    }
  };

  // Kopier opsætning fra event
  const [showCopyLayoutModal, setShowCopyLayoutModal] = useState(null); // target event
  const [copyLayoutSource, setCopyLayoutSource] = useState("");
  const [copyIncludeContainers, setCopyIncludeContainers] = useState(true);
  const [copyingLayout, setCopyingLayout] = useState(false);

  const doCopyLayout = async () => {
    if (!showCopyLayoutModal || !copyLayoutSource) { toast.error("Vælg et kilde-event"); return; }
    setCopyingLayout(true);
    try {
      const res = await axios.post(
        `${API}/events/${showCopyLayoutModal.id}/copy-layout-from/${copyLayoutSource}?include_containers=${copyIncludeContainers}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { copied_points, copied_maxicontainers, skipped_duplicates } = res.data;
      toast.success(`Kopieret: ${copied_points} punkter + ${copied_maxicontainers} maxicontainere${skipped_duplicates ? ` (${skipped_duplicates} sprunget over - findes allerede)` : ''}`);
      setShowCopyLayoutModal(null);
      setCopyLayoutSource("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kopiering fejlede");
    } finally {
      setCopyingLayout(false);
    }
  };

  const backfillQrRefs = async (eventId) => {
    try {
      const res = await axios.post(`${API}/events/${eventId}/backfill-qr-refs`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Tildelt ${res.data.assigned} QR-referencer (${res.data.prefix}-præfix)`);
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke tildele QR-referencer");
    }
  };

  const renameQrPrefix = async (eventId) => {
    const cleaned = (editQrPrefix || '').trim().toUpperCase();
    if (!/^[A-ZÆØÅ]{1,5}$/.test(cleaned)) {
      toast.error("Indtast et gyldigt præfix i QR-præfix-feltet først (kun bogstaver, max 5)");
      return;
    }
    try {
      toast.info(`Omdøber alle QR-refs → "${cleaned}"...`);
      const res = await axios.post(
        `${API}/events/${eventId}/rename-qr-prefix`,
        { new_prefix: cleaned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { renamed_points, renamed_maxicontainers, assigned_points, assigned_maxicontainers, skipped } = res.data;
      const totalRenamed = (renamed_points || 0) + (renamed_maxicontainers || 0);
      const totalAssigned = (assigned_points || 0) + (assigned_maxicontainers || 0);
      const parts = [];
      if (totalRenamed > 0) parts.push(`omdøbt ${renamed_points}p/${renamed_maxicontainers}m`);
      if (totalAssigned > 0) parts.push(`tildelt ${assigned_points}p/${assigned_maxicontainers}m nye`);
      if (skipped > 0) parts.push(`${skipped} allerede korrekte`);
      if (parts.length === 0) {
        toast.info(`Ingen punkter at ændre — eventet har ingen ressourcepunkter endnu.`);
      } else {
        toast.success(`✅ ${cleaned}-præfix: ${parts.join(', ')}`);
      }
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Omdøbning fejlede");
    }
  };

  const loadGuests = async (eventId) => {
    try {
      const res = await axios.get(`${API}/guests?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      setGuests(res.data);
    } catch { setGuests([]); }
  };

  const createGuest = async (eventId) => {
    const wantsDirect = !!newGuestDirectLogin;
    if (!wantsDirect && !newGuestEmail.trim()) {
      toast.error("Email er påkrævet ved sikker adgang");
      return;
    }
    try {
      // Konvertér valid_from (datetime-local) til ISO. Tomt = backend bruger 'nu'
      let validFromISO = undefined;
      if (newGuestValidFrom) {
        const dt = new Date(newGuestValidFrom);
        if (!isNaN(dt.getTime())) validFromISO = dt.toISOString();
      }
      // Beregn duration_hours — håndtér "Brugerdefineret dato" specifikt
      let durationHours;
      if (newGuestExpiry === 'custom') {
        durationHours = computeCustomDurationHours(validFromISO, newGuestCustomEndDate);
        if (!durationHours || durationHours < 1) {
          toast.error("Vælg en gyldig slutdato længere fremme i tiden");
          return;
        }
      } else if (newGuestExpiry === '' || newGuestExpiry == null) {
        durationHours = 24;
      } else {
        durationHours = parseInt(newGuestExpiry, 10) || 0;
      }
      const payload = {
        event_id: eventId,
        email: newGuestEmail.trim(),
        name: newGuestName.trim() || 'Gæst',
        duration_hours: durationHours,
        direct_login_enabled: wantsDirect,
        visible_layers: newGuestVisibleLayers,
      };
      if (validFromISO) payload.valid_from = validFromISO;
      const res = await axios.post(`${API}/guests`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(wantsDirect ? `Gæsteadgang oprettet — QR-link klar til print` : `Gæsteadgang oprettet! Kode: ${res.data.guest_code}`);
      resetGuestForm();
      loadGuests(eventId);
      // Åbn QR-print-dialog automatisk
      setQrPrintGuest(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved oprettelse");
    }
  };

  const deleteGuest = async (guestId, eventId) => {
    try {
      await axios.delete(`${API}/guests/${guestId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Gæsteadgang slettet");
      loadGuests(eventId);
    } catch { toast.error("Fejl ved sletning"); }
  };


  const showConfirm = (title, message, onConfirm, confirmText = 'Slet', confirmVariant = 'destructive') => {
    setConfirmDialog({ open: true, title, message, onConfirm, confirmText, confirmVariant });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Slet', confirmVariant: 'destructive' });
  };

  useEffect(() => {
    loadEvents();
    axios.get(`${API}/platform/settings`).then(r => setPlatformSettings(r.data)).catch(() => {});
  }, []);
  
  // Load demo sessions when user is available and is admin
  useEffect(() => {
    if (user?.role === 'admin') {
      loadDemoSessions();
      loadDemoLinks();
    }
  }, [user]);

  const loadEvents = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/events`, { headers }),
        axios.get(`${API}/events?include_archived=true`, { headers })
      ]);
      // Backend afgrænser allerede events efter brugerens tildelinger (admin ser alt).
      // Klientsiden bevarer gæste-filter som defense-in-depth.
      let active = activeRes.data;
      let archived = archivedRes.data.filter(e => e.archived);
      if (user?.role === 'gæst') {
        const allowed = new Set(user.assigned_event_ids || []);
        active = active.filter(e => allowed.has(e.id));
        archived = archived.filter(e => allowed.has(e.id));
      }
      setEvents(active);
      setArchivedEvents(archived);
    } catch (error) {
      console.error("Kunne ikke hente events", error);
    }
  };

  const loadDemoSessions = async () => {
    try {
      const res = await axios.get(`${API}/demo/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoSessions(res.data);
    } catch {
      // Ignorer fejl (ikke-admin)
    }
  };

  const loadDemoLinks = async () => {
    try {
      const res = await axios.get(`${API}/demo-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDemoLinks(res.data);
      const landing = res.data.find(l => l.is_landing_demo && l.active);
      setLandingDemoEventId(landing?.event_id || null);
    } catch {
      // Ignorer fejl
    }
  };

  const setLandingDemoEvent = async (eventId) => {
    try {
      // Find eksisterende demo-link for dette event, eller opret et nyt
      let link = demoLinks.find(l => l.event_id === eventId);
      if (!link) {
        const event = events.find(e => e.id === eventId);
        const res = await axios.post(`${API}/demo-links`, {
          event_id: eventId,
          event_name: event?.name || "Event",
          label: `${event?.name || "Event"} Demo`
        }, { headers: { Authorization: `Bearer ${token}` } });
        link = res.data;
      }
      // Sæt som forside-demo
      await axios.put(`${API}/demo-links/${link.id}/set-landing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Forside-demo sat til: ${events.find(e => e.id === eventId)?.name || eventId}`);
      setLandingDemoEventId(eventId);
      loadDemoLinks();
    } catch (err) {
      toast.error("Kunne ikke opdatere forside-demo");
      console.error(err);
    }
  };

  const createEvent = async () => {
    if (!newEventName.trim()) {
      toast.error("Indtast et navn");
      return;
    }
    setLoading(true);
    try {
      let eventId;
      
      // Tjek om vi kopierer fra et eksisterende event
      const anyCopyOption = Object.values(copyOptions).some(v => v);
      
      if (copyFromEventId && anyCopyOption) {
        // Kopier event med valgte moduler
        const copyRes = await axios.post(`${API}/events/copy`, {
          source_event_id: copyFromEventId,
          new_name: newEventName,
          new_description: newEventDesc,
          ...copyOptions
        }, { headers: { Authorization: `Bearer ${token}` }});
        
        eventId = copyRes.data.new_event_id;
        const copiedModules = copyRes.data.copied_modules;
        
        if (copiedModules.length > 0) {
          toast.success(`Event oprettet med: ${copiedModules.join(', ')}`);
        } else {
          toast.success("Event oprettet (ingen moduler kopieret)");
        }
      } else {
        // Opret nyt tomt event
        const eventRes = await axios.post(`${API}/events`, 
          { name: newEventName, description: newEventDesc },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        eventId = eventRes.data.id;
        toast.success("Event oprettet!");
      }
      
      // Hvis adresse er angivet, slå den op og gem lokationen
      if (newEventAddress.trim()) {
        try {
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newEventAddress)}&limit=1`
          );
          const geoData = await geoRes.json();
          
          if (geoData.length > 0) {
            const { lat, lon } = geoData[0];
            await axios.put(
              `${API}/events/${eventId}/location`,
              {
                lat: parseFloat(lat),
                lng: parseFloat(lon),
                zoom: 16,
                address: newEventAddress
              },
              { headers: { Authorization: `Bearer ${token}` }}
            );
          }
        } catch (geoError) {
          console.error("Geocoding fejl:", geoError);
        }
      }
      
      // Upload logo hvis valgt
      if (newEventLogo) {
        try {
          const logoForm = new FormData();
          logoForm.append('file', newEventLogo);
          await axios.post(`${API}/events/${eventId}/logo`, logoForm, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        } catch (logoErr) {
          console.error("Logo upload fejl:", logoErr);
        }
      }
      
      // Reset form
      setNewEventName("");
      setNewEventDesc("");
      setNewEventAddress("");
      setNewEventLogo(null);
      setCopyFromEventId("");
      setCopyOptions({
        copy_zones: false,
        copy_teams: false,
        copy_shifts: false,
        copy_volunteers: false,
        copy_materiel: false,
        copy_lager: false,
        copy_stamdata: false,
        copy_notifications: false,
        copy_messages: false,
        copy_info: false,
        copy_deadlines: false,
      });
      setShowCreate(false);
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved oprettelse");
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = (eventId, eventName) => {
    showConfirm(
      "Slet event permanent?",
      `Er du sikker på du vil slette "${eventName}" permanent? Dette sletter ALLE data inklusiv frivillige, materiel, lager osv. Denne handling kan IKKE fortrydes.`,
      async () => {
        closeConfirm();
        try {
          await axios.delete(`${API}/events/${eventId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Event slettet permanent");
          loadEvents();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
      }
    );
  };

  const archiveEvent = (eventId, eventName) => {
    showConfirm(
      "Arkiver event?",
      `"${eventName}" flyttes til arkivet. Eventet og al data bevares, men fjernes fra oversigten. Du kan genaktivere det senere.`,
      async () => {
        closeConfirm();
        try {
          await axios.put(`${API}/events/${eventId}/archive`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Event arkiveret");
          // Hvis det arkiverede event er det valgte, nulstil valget
          if (localStorage.getItem('selectedEventId') === eventId) {
            localStorage.removeItem('selectedEventId');
            localStorage.removeItem('selectedEventName');
            localStorage.removeItem('selectedEventArchived');
            localStorage.removeItem('selectedEventLogoUrl');
            if (onSelectEvent) onSelectEvent(null);
            window.location.reload();
          }
          loadEvents();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved arkivering");
        }
      },
      "Arkiver",  // Custom confirm button text
      "default"   // Non-destructive variant
    );
  };

  const unarchiveEvent = async (eventId) => {
    try {
      await axios.put(`${API}/events/${eventId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Event genaktiveret");
      loadEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved genaktivering");
    }
  };

  // Logo upload for eksisterende event
  const handleLogoUpload = async (eventId, file) => {
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/events/${eventId}/logo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Logo uploadet!");
      loadEvents();
    } catch (err) {
      toast.error("Fejl ved logo upload");
    }
  };

  // Åbn rediger event dialog
  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEditName(event.name || "");
    setEditDesc(event.description || "");
    setEditAddress(event.default_location?.address || "");
    setEditQrPrefix(event.qr_prefix || "");
  };

  // Gem event-ændringer
  const saveEditEvent = async () => {
    if (!editName.trim()) {
      toast.error("Navn kan ikke være tomt");
      return;
    }
    setEditLoading(true);
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, {
        name: editName,
        description: editDesc,
        address: editAddress,
        qr_prefix: editQrPrefix.trim().toUpperCase() || null,
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Event opdateret!");
      setEditingEvent(null);
      loadEvents();
      // Opdater localStorage hvis det er det valgte event
      if (localStorage.getItem('selectedEventId') === editingEvent.id) {
        localStorage.setItem('selectedEventName', editName);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved opdatering");
    } finally {
      setEditLoading(false);
    }
  };

  // Rediger event logo (via rediger-dialogen)
  const handleEditLogoUpload = async (file) => {
    if (!editingEvent) return;
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/events/${editingEvent.id}/logo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Logo opdateret!");
      loadEvents();
    } catch (err) {
      toast.error("Fejl ved logo upload");
    }
  };

  // Download QR-kode
  const downloadQr = async (eventId, eventName, type = 'event') => {
    try {
      const baseUrl = window.location.origin;
      const url = type === 'universal' 
        ? `${API}/qr-code?base_url=${encodeURIComponent(baseUrl)}`
        : `${API}/events/${eventId}/qr-code?base_url=${encodeURIComponent(baseUrl)}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = type === 'universal' ? 'resovia-qr-universel.png' : `qr-${eventName}.png`;
      link.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("QR-kode downloadet!");
    } catch (err) {
      toast.error("Fejl ved QR-kode download");
    }
  };

  // Kopier event-link
  const copyEventLink = (eventId) => {
    const url = `${window.location.origin}?event=${eventId}`;
    safeCopy(url)
      .then(() => toast.success("Link kopieret til udklipsholder!"))
      .catch(() => toast.error("Kunne ikke kopiere — kopier manuelt: " + url));
  };

  // Send invitation email
  const sendInvite = async () => {
    if (!inviteEmail || !inviteName) {
      toast.error("Udfyld email og navn");
      return;
    }
    setSendingInvite(true);
    try {
      await axios.post(`${API}/events/${showInviteModal}/send-invite`, {
        to_email: inviteEmail,
        to_name: inviteName,
        event_id: showInviteModal,
        role: inviteRole,
        custom_message: inviteMessage || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invitation sendt til ${inviteEmail}!`);
      setShowInviteModal(null);
      setInviteEmail("");
      setInviteName("");
      setInviteRole("admin_assistant");
      setInviteMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fejl ved afsendelse");
    } finally {
      setSendingInvite(false);
    }
  };

  const selectEvent = (event) => {
    localStorage.setItem("selectedEventId", event.id);
    localStorage.setItem("selectedEventName", event.name);
    localStorage.setItem("selectedEventArchived", event.archived ? "true" : "false");
    if (event.logo_url) localStorage.setItem("selectedEventLogoUrl", event.logo_url);
    else localStorage.removeItem("selectedEventLogoUrl");
    if (onSelectEvent) onSelectEvent({ ...event, archived: !!event.archived });
    navigate("/");
  };

  const isAdmin = user?.role === 'admin';
  const isAdminOrAssistant = user?.role === 'admin' || user?.role === 'admin_assistant';

  return (
    <div className="min-h-screen bg-background" data-testid="event-selector-page">
      {/* Bekræftelses-dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
        confirmText={confirmDialog.confirmText}
        confirmVariant={confirmDialog.confirmVariant}
      />
      
      <header className="border-b border-border bg-card/50 backdrop-blur-xl">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">
              Resovia
            </h1>
            <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
              Vælg Event
            </p>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/idebank')}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Idébank
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/brugsvejledning')}
              title="Brugsvejledning"
              data-testid="guide-btn"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            <button 
              onClick={() => navigate('/profil')} 
              className="text-right hover:opacity-70 transition-opacity"
              title="Min profil"
              data-testid="profile-link"
            >
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground font-mono uppercase">{user?.role}</p>
            </button>
            <ThemeToggle />
            <Button onClick={onLogout} variant="ghost" size="sm">
              Log ud
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl font-semibold font-heading uppercase mb-2">
              Dine Events
            </h2>
            <p className="text-muted-foreground text-sm">
              Vælg et event for at administrere ressourcer og zoner
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Universel QR-kode download */}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadQr(null, null, 'universal')}
                className="gap-2"
                data-testid="universal-qr-btn"
                title="Download universel QR-kode til login"
              >
                <QrCode className="h-4 w-4" />
                Universel QR
              </Button>
            )}
            {isAdmin && archivedEvents.length > 0 && (
              <Button
                variant={showArchived ? "secondary" : "outline"}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="gap-2"
                data-testid="toggle-archived-btn"
              >
                <Archive className="h-4 w-4" />
                Arkiv ({archivedEvents.length})
              </Button>
            )}
            {isAdmin && (
              <Button 
                onClick={() => setShowCreate(!showCreate)}
                className="bg-primary text-primary-foreground"
                data-testid="create-event-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Opret Event
              </Button>
            )}
          </div>
        </div>

        {/* Opret Event Form */}
        {showCreate && isAdmin && (
          <div className="mb-8 p-6 bg-card border border-border rounded-lg" data-testid="create-event-form">
            <h3 className="text-lg font-bold mb-4">Opret nyt Event</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                  Event Navn *
                </label>
                <Input
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  placeholder="F.eks. Heartland 2026"
                  className="mt-1"
                  data-testid="event-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                  Beskrivelse (valgfrit)
                </label>
                <Input
                  value={newEventDesc}
                  onChange={(e) => setNewEventDesc(e.target.value)}
                  placeholder="Kort beskrivelse af eventet"
                  className="mt-1"
                  data-testid="event-desc-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  Adresse / Lokation (valgfrit)
                </label>
                <Input
                  value={newEventAddress}
                  onChange={(e) => setNewEventAddress(e.target.value)}
                  placeholder="F.eks. 'Egeskov Slot' eller 'Nibe, Denmark'"
                  className="mt-1"
                  data-testid="event-address-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Kortet vil automatisk centrere på denne adresse når du åbner Oversigtskort
                </p>
              </div>
              
              {/* Logo upload */}
              <div>
                <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                  <Image className="h-3 w-3" />
                  Event-logo (valgfrit)
                </label>
                <div className="mt-1 flex items-center gap-3">
                  {newEventLogo && (
                    <img src={URL.createObjectURL(newEventLogo)} alt="Preview" className="h-12 w-12 object-contain rounded border border-border bg-zinc-800 p-1" />
                  )}
                  <label className="cursor-pointer">
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => setNewEventLogo(e.target.files?.[0] || null)}
                      data-testid="event-logo-input"
                    />
                    <span className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-border rounded text-sm text-muted-foreground hover:text-white hover:border-primary transition-colors">
                      <Upload className="h-3 w-3" />
                      {newEventLogo ? 'Skift logo' : 'Vælg logo'}
                    </span>
                  </label>
                  {newEventLogo && (
                    <Button variant="ghost" size="sm" onClick={() => setNewEventLogo(null)} className="text-muted-foreground">
                      Fjern
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Event Kopiering */}
              {events.length > 0 && (
                <div className="border-t border-border pt-4 mt-4">
                  <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2 mb-2">
                    <Copy className="h-3 w-3" />
                    Kopier fra eksisterende event (valgfrit)
                  </label>
                  <select
                    value={copyFromEventId}
                    onChange={(e) => setCopyFromEventId(e.target.value)}
                    className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="copy-from-event-select"
                  >
                    <option value="">Start fra bunden (tomt event)</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  
                  {copyFromEventId && (
                    <div className="mt-3 p-3 bg-zinc-900 rounded border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-muted-foreground">Vælg hvilke moduler der skal kopieres:</p>
                        <button
                          type="button"
                          className="text-xs text-primary hover:text-primary/80 underline"
                          onClick={() => {
                            const allSelected = Object.values(copyOptions).every(v => v);
                            const newVal = !allSelected;
                            setCopyOptions({
                              copy_zones: newVal, copy_teams: newVal, copy_shifts: newVal,
                              copy_volunteers: newVal, copy_materiel: newVal, copy_lager: newVal,
                              copy_stamdata: newVal, copy_notifications: newVal, copy_messages: newVal,
                              copy_info: newVal, copy_deadlines: newVal,
                            });
                          }}
                          data-testid="copy-toggle-all"
                        >
                          {Object.values(copyOptions).every(v => v) ? 'Fravælg alle' : 'Vælg alle'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'copy_zones', label: 'Kort & Zoner', desc: 'Alle punkter og zoner' },
                          { key: 'copy_teams', label: 'Teams & Funktioner', desc: 'Roller, områder, arbejdszoner' },
                          { key: 'copy_shifts', label: 'Vagter', desc: 'Vagtskemaer (uden tildelinger)' },
                          { key: 'copy_volunteers', label: 'Frivillige', desc: 'Navne, emails (nulstillet vagter)' },
                          { key: 'copy_materiel', label: 'Materiel', desc: 'Basisliste til bestilling' },
                          { key: 'copy_lager', label: 'Lager', desc: 'Nedpakning fra forrige event' },
                          { key: 'copy_stamdata', label: 'Stamdata', desc: 'Containere, fraktioner, typer' },
                          { key: 'copy_notifications', label: 'Notifikationsregler', desc: 'Automatiske notifikationer' },
                          { key: 'copy_messages', label: 'Planlagte beskeder', desc: 'Forudplanlagte beskeder' },
                          { key: 'copy_info', label: 'Vigtige info', desc: 'Info-dokumenter & vejledninger' },
                          { key: 'copy_deadlines', label: 'Deadlines', desc: 'Opgaver & tidsfrister' },
                        ].map(opt => (
                          <label 
                            key={opt.key}
                            className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                              copyOptions[opt.key] ? 'bg-primary/20 border border-primary/50' : 'hover:bg-zinc-800'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={copyOptions[opt.key]}
                              onChange={(e) => setCopyOptions({...copyOptions, [opt.key]: e.target.checked})}
                              className="mt-0.5 rounded"
                            />
                            <div>
                              <span className="text-sm font-medium flex items-center gap-1">
                                {copyOptions[opt.key] && <Check className="h-3 w-3 text-primary" />}
                                {opt.label}
                              </span>
                              <span className="text-xs text-muted-foreground">{opt.desc}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <Button 
                  onClick={createEvent} 
                  disabled={loading}
                  data-testid="submit-event-btn"
                >
                  {loading ? "Opretter..." : "Opret Event"}
                </Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Annuller
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Event Liste */}
        {events.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-lg">
            <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Ingen events endnu</h3>
            <p className="text-muted-foreground mb-4">
              {isAdmin ? "Opret dit første event for at komme i gang" : "Kontakt en administrator for at oprette events"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-all group"
                data-testid={`event-card-${event.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Event logo */}
                      {event.logo_url ? (
                        <img 
                          src={`${API.replace('/api', '')}${event.logo_url}`} 
                          alt={event.name} 
                          className="h-12 w-12 object-contain rounded border border-border bg-zinc-800 p-1 shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded border border-border bg-zinc-800 flex items-center justify-center shrink-0">
                          <Calendar className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-lg font-bold font-heading uppercase">
                          {event.name}
                        </h3>
                        {event.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {isAdmin && (
                        <>
                          {/* Rediger event */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditEvent(event);
                            }}
                            className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
                            title="Rediger event"
                            data-testid={`edit-event-${event.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {/* Arkiver */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              archiveEvent(event.id, event.name);
                            }}
                            className="text-muted-foreground hover:text-foreground h-8 w-8 p-0"
                            title="Arkiver event"
                            data-testid={`archive-event-${event.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <FileSpreadsheet className="h-3 w-3" />
                      {event.has_excel ? "Excel tilknyttet" : "Ingen Excel"}
                    </div>
                    {event.default_location?.address && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {event.default_location.address}
                      </div>
                    )}
                  </div>

                  {/* Distribution: QR + Link - kun admin & admin assistent */}
                  {isAdminOrAssistant && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); downloadQr(event.id, event.name, 'event'); }}
                      className="text-xs"
                      title="Download QR-kode for dette event"
                      data-testid={`qr-event-${event.id}`}
                    >
                      <QrCode className="h-3 w-3 mr-1" />
                      QR-kode
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); copyEventLink(event.id); }}
                      className="text-xs"
                      title="Kopier direkte link"
                      data-testid={`link-event-${event.id}`}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Kopier link
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setShowInviteModal(event.id); }}
                      className="text-xs"
                      title="Send invitation via email til medarbejder/leder"
                      data-testid={`invite-event-${event.id}`}
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Inviter
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setShowGuestModal(event.id); loadGuests(event.id); }}
                      className="text-xs"
                      title="Opret/administrer gæsteadgange (frivillige med QR-kode)"
                      data-testid={`guest-access-${event.id}`}
                    >
                      <Users className="h-3 w-3 mr-1" />
                      Gæsteadgang
                    </Button>
                  </div>
                  )}

                  <Button
                    onClick={() => selectEvent(event)}
                    className="w-full bg-primary text-primary-foreground group-hover:shadow-lg transition-all"
                    data-testid={`select-event-${event.id}`}
                  >
                    Åbn Event
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Arkiverede Events */}
        {showArchived && archivedEvents.length > 0 && (
          <div className="mt-8" data-testid="archived-events-section">
            <h3 className="text-lg font-semibold font-heading uppercase mb-4 flex items-center gap-2 text-muted-foreground">
              <Archive className="h-5 w-5" />
              Arkiverede Events
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {archivedEvents.map((event) => (
                <div
                  key={event.id}
                  className="bg-card/50 border border-border rounded-lg overflow-hidden opacity-70 hover:opacity-100 transition-all"
                  data-testid={`archived-event-card-${event.id}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold font-heading uppercase">
                            {event.name}
                          </h3>
                          <span className="text-[10px] uppercase tracking-wider font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded">
                            Arkiveret
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => selectEvent(event)}
                        className="flex-1"
                        data-testid={`open-archived-event-${event.id}`}
                      >
                        Åbn (read-only)
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unarchiveEvent(event.id)}
                        title="Genaktiver event"
                        data-testid={`unarchive-event-${event.id}`}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteEvent(event.id, event.name)}
                        className="text-destructive hover:bg-destructive/10"
                        title="Slet permanent"
                        data-testid={`delete-archived-event-${event.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Forside Demo-event vælger (kun admin) */}
        {isAdmin && events.length > 0 && (
          <div className="mt-8" data-testid="landing-demo-section">
            <h3 className="text-lg font-semibold font-heading uppercase mb-4 flex items-center gap-2 text-muted-foreground">
              <Link2 className="h-5 w-5" />
              Forside Demo-event
            </h3>
            <div className="bg-card/50 border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-3">
                Vælg hvilket event "Prøv Demo"-knappen på forsiden skal henvise til:
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {events.map((event) => (
                  <Button
                    key={event.id}
                    variant={landingDemoEventId === event.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLandingDemoEvent(event.id)}
                    className={landingDemoEventId === event.id 
                      ? "bg-[#5B8C2A] hover:bg-[#4a7322] text-white" 
                      : "text-muted-foreground hover:text-white"}
                    data-testid={`set-demo-event-${event.id}`}
                  >
                    {landingDemoEventId === event.id && <Check className="h-3.5 w-3.5 mr-1.5" />}
                    {event.name}
                  </Button>
                ))}
              </div>
              {landingDemoEventId && (
                <p className="text-xs text-[#5B8C2A] mt-2">
                  Aktiv: {events.find(e => e.id === landingDemoEventId)?.name || "Ukendt"}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Aktive Demo-sessions (kun admin) */}
        {isAdmin && demoSessions.length > 0 && (
          <div className="mt-8" data-testid="demo-sessions-section">
            <h3 className="text-lg font-semibold font-heading uppercase mb-4 flex items-center gap-2 text-muted-foreground">
              <Play className="h-5 w-5" />
              Aktive Demoer ({demoSessions.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {demoSessions.map((session) => (
                <div
                  key={session.id}
                  className="bg-card/50 border border-[#5B8C2A]/30 rounded-lg p-4 flex items-center justify-between"
                  data-testid={`demo-session-${session.id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-white">Demo #{session.id.slice(0, 6)}</p>
                    <p className="text-xs text-muted-foreground">
                      Startet {new Date(session.created_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-mono px-2 py-0.5 rounded ${
                      session.remaining_seconds < 300 ? 'bg-red-500/20 text-red-400' : 'bg-[#5B8C2A]/20 text-[#5B8C2A]'
                    }`} data-testid={`demo-timer-${session.id}`}>
                      {session.remaining_display}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        await axios.post(`${API}/demo/end/${session.id}`);
                        toast.success("Demo-session afsluttet");
                        loadDemoSessions();
                      }}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400"
                      title="Afslut demo"
                      data-testid={`end-demo-${session.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Powered by footer */}
        {platformSettings?.powered_by_enabled && (
          <div className="mt-8 mb-4 text-center" data-testid="powered-by-footer">
            <div className="inline-flex items-center gap-2 text-[11px] text-muted-foreground/40">
              {platformSettings.host_logo_url && (
                <img 
                  src={`${API.replace('/api', '')}${platformSettings.host_logo_url}`}
                  alt={platformSettings.host_name}
                  className="h-4 w-auto opacity-40"
                />
              )}
              <span>
                {platformSettings.platform_name || 'Resovia'} &mdash;{' '}
                {platformSettings.powered_by_text || 'powered by'}{' '}
                <span className="font-semibold">{platformSettings.host_name}</span>
              </span>
            </div>
          </div>
        )}
      </main>
      
      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInviteModal(null)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <div className="p-6">
              <h2 className="text-lg font-bold font-heading uppercase mb-1 flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Send invitation
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {events.find(e => e.id === showInviteModal)?.name || 'Event'}
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Navn</Label>
                  <Input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Fx Anders Jensen" data-testid="invite-name-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Email</Label>
                  <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="email@example.com" data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Rolle</Label>
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                    data-testid="invite-role-select"
                  >
                    <option value="admin_assistant">Administratorassistent (arrangør)</option>
                    <option value="leader">Leder</option>
                    <option value="volunteer">Frivillig</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Personlig besked (valgfrit)</Label>
                  <textarea
                    value={inviteMessage}
                    onChange={e => setInviteMessage(e.target.value)}
                    placeholder="Tilføj en personlig besked..."
                    rows={3}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                    data-testid="invite-message-input"
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-border p-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowInviteModal(null)}>Annuller</Button>
              <Button size="sm" onClick={sendInvite} disabled={sendingInvite || !inviteEmail || !inviteName} data-testid="send-invite-btn">
                <Send className="h-4 w-4 mr-2" />
                {sendingInvite ? 'Sender...' : 'Send invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Rediger Event Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingEvent(null)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="edit-event-modal">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold font-heading uppercase flex items-center gap-2">
                  <Pencil className="h-5 w-5 text-primary" />
                  Rediger Event
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setEditingEvent(null)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Navn *</Label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Event-navn"
                    data-testid="edit-event-name"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Beskrivelse</Label>
                  <Input
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Kort beskrivelse af eventet"
                    data-testid="edit-event-desc"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-3 w-3" />
                    Adresse / Lokation
                  </Label>
                  <Input
                    value={editAddress}
                    onChange={(e) => setEditAddress(e.target.value)}
                    placeholder="F.eks. 'Egeskov Slot' eller 'Nibe, Denmark'"
                    data-testid="edit-event-address"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Adressen bruges til at centrere kortet automatisk
                  </p>
                </div>
                
                {/* Logo upload i rediger */}
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                    <Image className="h-3 w-3" />
                    Event-logo
                  </Label>
                  <div className="mt-1 flex items-center gap-3">
                    {editingEvent.logo_url && (
                      <img 
                        src={`${API.replace('/api', '')}${editingEvent.logo_url}`} 
                        alt="Logo" 
                        className="h-12 w-12 object-contain rounded border border-border bg-zinc-800 p-1"
                      />
                    )}
                    <label className="cursor-pointer">
                      <Input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) handleEditLogoUpload(e.target.files[0]);
                        }}
                        data-testid="edit-event-logo-input"
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-border rounded text-sm text-muted-foreground hover:text-white hover:border-primary transition-colors">
                        <Upload className="h-3 w-3" />
                        {editingEvent.logo_url ? 'Skift logo' : 'Upload logo'}
                      </span>
                    </label>
                  </div>
                </div>

                {/* QR-præfix til genbrugelige QR-koder */}
                <div>
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-2">
                    QR-præfix
                  </Label>
                  <Input
                    value={editQrPrefix}
                    onChange={(e) => setEditQrPrefix(e.target.value.toUpperCase().slice(0, 5))}
                    placeholder="F.eks. HL (Heartland) eller NB (Nibe)"
                    maxLength={5}
                    className="font-mono"
                    data-testid="edit-event-qr-prefix"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Bruges til genbrugelige QR-koder (fx "HL-001" til Heartland). Max 5 tegn. <br/>
                    <span className="text-primary">💡 Skift præfix her + klik "Omdøb alle QR-refs" nederst for at omdøbe eksisterende punkter.</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-border p-4 flex flex-wrap justify-between gap-2">
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => { setShowCopyLayoutModal(editingEvent); setEditingEvent(null); }} data-testid="open-copy-layout-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Kopier opsætning fra event
                </Button>
                <Button variant="outline" size="sm" onClick={() => backfillQrRefs(editingEvent.id)} data-testid="backfill-qr-refs-btn">
                  Tildel manglende QR-refs
                </Button>
                <Button variant="outline" size="sm" onClick={() => renameQrPrefix(editingEvent.id)} data-testid="rename-qr-prefix-btn">
                  Omdøb alle QR-refs
                </Button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditingEvent(null)}>Annuller</Button>
                <Button size="sm" onClick={saveEditEvent} disabled={editLoading || !editName.trim()} data-testid="save-edit-event-btn">
                  <Check className="h-4 w-4 mr-2" />
                  {editLoading ? 'Gemmer...' : 'Gem ændringer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gæsteadgang modal */}
      {showGuestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { resetGuestForm(); setShowGuestModal(null); }}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-border">
              <h3 className="font-bold text-lg">Gæsteadgange</h3>
              <p className="text-sm text-muted-foreground">Du bestemmer hvilke kort-lag hver gæst må se.</p>
            </div>
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Opret/rediger gæst */}
              <div className={`rounded-lg p-3 space-y-3 ${editingGuest ? 'bg-blue-500/10 border border-blue-500/40' : 'bg-muted/30'}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-mono uppercase font-bold">
                    {editingGuest ? `Redigér: ${editingGuest.name}` : 'Opret ny gæsteadgang'}
                  </p>
                  {editingGuest && (
                    <button
                      type="button"
                      onClick={resetGuestForm}
                      className="text-[10px] text-muted-foreground hover:text-white underline"
                      data-testid="cancel-edit-guest-btn"
                    >
                      Annullér
                    </button>
                  )}
                </div>
                {editingGuest && (
                  <p className="text-[10px] text-muted-foreground -mt-1">
                    QR-kode, email og 6-cifret kode bevares — kun de ændrede felter opdateres.
                  </p>
                )}

                <div className="flex gap-2">
                  <input
                    value={newGuestName}
                    onChange={e => setNewGuestName(e.target.value)}
                    placeholder="Navn / hold (vises på QR-print)"
                    className="flex-1 h-8 rounded border border-input bg-input/50 px-2 text-sm"
                    data-testid="new-guest-name-input"
                  />
                  <input
                    value={newGuestEmail}
                    onChange={e => setNewGuestEmail(e.target.value)}
                    placeholder={newGuestDirectLogin ? "(valgfri ved direkte login)" : "gaest@event.dk"}
                    disabled={newGuestDirectLogin && false}
                    className="flex-[1.5] h-8 rounded border border-input bg-input/50 px-2 text-sm"
                    data-testid="new-guest-email-input"
                  />
                </div>

                {/* Adgangstype: Direct vs Sikker */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewGuestDirectLogin(true)}
                    className={`p-2 rounded text-left text-xs border ${newGuestDirectLogin ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card'}`}
                    data-testid="guest-mode-direct-btn"
                  >
                    <div className="font-bold mb-0.5">Direkte adgang via QR</div>
                    <div className="text-[10px] text-muted-foreground">Scan = automatisk login. Ideelt til 1000+ frivillige.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewGuestDirectLogin(false)}
                    className={`p-2 rounded text-left text-xs border ${!newGuestDirectLogin ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card'}`}
                    data-testid="guest-mode-secure-btn"
                  >
                    <div className="font-bold mb-0.5">Sikker adgang (email + kode)</div>
                    <div className="text-[10px] text-muted-foreground">Til særligt udvalgte gæster med adgang til containere/GPS.</div>
                  </button>
                </div>

                {/* Gyldig fra + varighed */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-muted-foreground uppercase font-mono">Gyldig fra</label>
                    <input
                      type="datetime-local"
                      value={newGuestValidFrom}
                      onChange={e => setNewGuestValidFrom(e.target.value)}
                      min="2024-01-01T00:00"
                      max="2099-12-31T23:59"
                      className="w-full h-8 rounded border border-input bg-input/50 px-2 text-sm"
                      data-testid="new-guest-valid-from-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Tomt = nu</p>
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground uppercase font-mono">Varighed</label>
                    <select
                      value={newGuestExpiry}
                      onChange={e => setNewGuestExpiry(e.target.value)}
                      className="w-full h-8 rounded border border-input bg-input/50 px-2 text-sm"
                      data-testid="new-guest-duration-input"
                    >
                      <option value="4">4 timer</option>
                      <option value="8">8 timer (én vagt)</option>
                      <option value="12">12 timer</option>
                      <option value="24">24 timer</option>
                      <option value="48">2 dage</option>
                      <option value="72">3 dage</option>
                      <option value="168">1 uge</option>
                      <option value="720">30 dage</option>
                      <option value="custom">Brugerdefineret dato…</option>
                    </select>
                    {newGuestExpiry === 'custom' && (
                      <input
                        type="date"
                        value={newGuestCustomEndDate}
                        onChange={e => setNewGuestCustomEndDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                        max="2099-12-31"
                        className="w-full h-8 mt-1 rounded border border-input bg-input/50 px-2 text-sm"
                        data-testid="new-guest-custom-end-date-input"
                      />
                    )}
                  </div>
                </div>

                {/* Synlige kort-lag */}
                <div>
                  <label className="text-[11px] text-muted-foreground uppercase font-mono">Hvilke kort-lag må gæsten se?</label>
                  <p className="text-[10px] text-muted-foreground mb-1">
                    Service-punkter (vand, pausetelt, snack, kaffe & te, boder) er altid synlige.
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      { key: 'resource_bins', label: 'Ressourcespande' },
                      { key: 'resource_islands', label: 'Ressourceøer' },
                      { key: 'maxicontainers', label: 'Maxicontainere' },
                      { key: 'work_zones', label: 'Arbejdsområder' },
                      { key: 'zones', label: 'Zoner / områder' },
                      { key: 'gps_trackers', label: 'GPS-trackere (biler m.m.)' },
                      { key: 'food_drinks', label: 'Boder & barer' },
                      { key: 'attention_points', label: 'Må placere opmærksomhedspunkter' },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 px-2 py-1.5 rounded bg-card border border-border cursor-pointer text-xs hover:border-primary/50">
                        <input
                          type="checkbox"
                          checked={!!newGuestVisibleLayers[key]}
                          onChange={e => setNewGuestVisibleLayers(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="h-3.5 w-3.5"
                          data-testid={`guest-layer-${key}`}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  {/* Adgang til Analytics-modul (kun-kig, statistikker) */}
                  <label className="flex items-center gap-2 mt-2 px-2 py-1.5 rounded bg-primary/5 border border-primary/30 cursor-pointer text-xs hover:border-primary/60">
                    <input
                      type="checkbox"
                      checked={!!newGuestVisibleLayers.analytics_module}
                      onChange={e => setNewGuestVisibleLayers(prev => ({ ...prev, analytics_module: e.target.checked }))}
                      className="h-3.5 w-3.5"
                      data-testid="guest-layer-analytics_module"
                    />
                    <span className="flex-1">
                      <span className="font-medium">Analytics-modul</span>
                      <span className="text-muted-foreground"> (kun kig — statistik & grafer)</span>
                    </span>
                  </label>
                </div>

                {editingGuest ? (
                  <Button size="sm" className="w-full" onClick={() => saveGuestEdit(showGuestModal)} data-testid="save-guest-edit-btn">
                    <Check className="h-3 w-3 mr-1" />Gem ændringer
                  </Button>
                ) : (
                  <Button size="sm" className="w-full" onClick={() => createGuest(showGuestModal)} data-testid="create-guest-btn">
                    <Plus className="h-3 w-3 mr-1" />Opret gæsteadgang
                  </Button>
                )}
              </div>

              {/* Eksisterende gæster */}
              {guests.length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-4">Ingen gæsteadgange endnu</p>
              )}
              {guests.map(guest => (
                <div key={guest.id} className="bg-card border border-border rounded-lg p-3" data-testid={`guest-${guest.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-bold text-sm">{guest.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {guest.direct_login_enabled ? '🔓 Direkte adgang via QR' : '🔐 Sikker (email + kode)'}
                      </p>
                      <p className="text-xs text-muted-foreground">{guest.email}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Rediger gæsteadgang" onClick={() => startEditGuest(guest)} data-testid={`guest-edit-${guest.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2" title="Vis QR-kode" onClick={() => setQrPrintGuest(guest)} data-testid={`guest-qr-${guest.id}`}>
                        <QrCode className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 h-7 w-7 p-0" onClick={() => deleteGuest(guest.id, showGuestModal)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {!guest.direct_login_enabled && (
                    <div className="flex items-center gap-2 bg-muted/30 rounded p-2">
                      <span className="text-xs text-muted-foreground">Kode:</span>
                      <span className="font-mono font-bold text-lg tracking-widest">{guest.guest_code || '------'}</span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 ml-auto" onClick={() => {
                        safeCopy(guest.guest_code || '')
                          .then(() => toast.success("Kode kopieret!"))
                          .catch(() => toast.error("Kunne ikke kopiere — kode: " + (guest.guest_code || '')));
                      }}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-1 space-y-0.5">
                    {guest.valid_from && (
                      <p>Gyldig fra: {new Date(guest.valid_from).toLocaleString('da-DK')}</p>
                    )}
                    {(guest.valid_until || guest.code_expires_at) ? (
                      <p>Udløber: {new Date(guest.valid_until || guest.code_expires_at).toLocaleString('da-DK')}</p>
                    ) : (
                      <p className="text-green-500">Udløber ikke</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-border flex justify-end">
              <Button variant="outline" size="sm" onClick={() => { resetGuestForm(); setShowGuestModal(null); }}>Luk</Button>
            </div>
          </div>
        </div>
      )}

      {/* QR-print modal til gæsteadgang */}
      {qrPrintGuest && (
        <GuestQrPrintModal
          guest={qrPrintGuest}
          onClose={() => setQrPrintGuest(null)}
          backendUrl={typeof window !== 'undefined' ? window.location.origin : ''}
          eventName={(events.find(e => e.id === showGuestModal) || events.find(e => (qrPrintGuest.assigned_event_ids || []).includes(e.id)) || {}).name || ''}
        />
      )}

      {/* Kopier opsætning fra event modal */}
      {showCopyLayoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !copyingLayout && setShowCopyLayoutModal(null)}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()} data-testid="copy-layout-modal">
            <div className="p-4 border-b border-border">
              <h3 className="font-bold text-lg">Kopier opsætning fra andet event</h3>
              <p className="text-sm text-muted-foreground">Kopierer ressourceøer, ressourcespande og maxicontainere (inkl. QR-refs) til <b>{showCopyLayoutModal.name}</b></p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Kilde-event</Label>
                <select
                  value={copyLayoutSource}
                  onChange={(e) => setCopyLayoutSource(e.target.value)}
                  className="mt-1 w-full h-10 rounded border border-input bg-input/50 px-2"
                  data-testid="copy-layout-source-select"
                >
                  <option value="">Vælg event...</option>
                  {events.filter(e => e.id !== showCopyLayoutModal.id).map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={copyIncludeContainers}
                  onChange={(e) => setCopyIncludeContainers(e.target.checked)}
                  className="mt-1"
                  data-testid="copy-layout-include-containers"
                />
                <div>
                  <div className="text-sm font-medium">Inkluder containere og fraktioner</div>
                  <p className="text-xs text-muted-foreground">Hvis ikke markeret, kopieres kun selve punkternes placering + navn + QR-ref (fraktioner tilføjes manuelt)</p>
                </div>
              </label>
              <div className="text-xs text-muted-foreground bg-muted/30 rounded p-2">
                Eksisterende punkter med samme navn bliver <b>sprunget over</b> — de får ikke dubletter.
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCopyLayoutModal(null)} disabled={copyingLayout}>Annuller</Button>
              <Button size="sm" onClick={doCopyLayout} disabled={copyingLayout || !copyLayoutSource} data-testid="copy-layout-confirm-btn">
                <Copy className="h-4 w-4 mr-2" />
                {copyingLayout ? 'Kopierer...' : 'Kopier nu'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
